import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import programService from '../../services/programService';
import { authService } from '../../services/authService';
import ProgramVideoPlayer from '../../components/ProgramVideoPlayer';
import '../../styles/programs/ProgramDetailPage.css';

const API_URL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5000');

export function ProgramDetailPage() {
  const { id } = useParams();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProgram = async () => {
      try {
        setLoading(true);
        const data = await programService.getProgramById(id);
        setProgram(data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching program:', err);
        setError('Failed to load program');
        setLoading(false);
      }
    };

    if (id) {
      fetchProgram();
    }
  }, [id]);

  const introVideoUrl = useMemo(() => {
    if (!program?.intro_video_url) return null;
    let u = (program.intro_video_url || '').trim();
    if (u.startsWith('//')) return `https:${u}`;
    if (u.startsWith('/')) return `${API_URL}${u}`;
    if (!u.startsWith('http')) return `https://${u}`;
    return u;
  }, [program?.intro_video_url]);

  const buildEmbedUrl = (url) => {
    if (!url) return null;
    try {
      const normalized = url.startsWith('http') ? url : `https://${url}`;
      const u = new URL(normalized);
      const host = u.hostname.toLowerCase();
      if (host.includes('youtube.com')) {
        const vid = u.searchParams.get('v');
        if (!vid) return null;
        const start = u.searchParams.get('t') || u.searchParams.get('start');
        return `https://www.youtube.com/embed/${vid}${start ? `?start=${start.replace('s', '')}` : ''}`;
      }
      if (host === 'youtu.be') {
        const vid = u.pathname.replace('/', '');
        const start = u.searchParams.get('t') || u.searchParams.get('start');
        return `https://www.youtube.com/embed/${vid}${start ? `?start=${start.replace('s', '')}` : ''}`;
      }
      if (host.includes('vimeo.com')) {
        const vid = u.pathname.split('/').filter(Boolean).pop();
        return vid ? `https://player.vimeo.com/video/${vid}` : null;
      }
      return null;
    } catch {
      return null;
    }
  };

  const introEmbedUrl = useMemo(() => buildEmbedUrl(program?.intro_video_url), [program?.intro_video_url]);
  const introPlayback = program?.intro_playback;
  const showIntro =
    introPlayback?.url ||
    introEmbedUrl ||
    introVideoUrl ||
    introPlayback?.type === 'processing' ||
    introPlayback?.type === 'hls';

  const introVideoWidth = useMemo(() => {
    const parsed = parseInt(program?.intro_video_width, 10);
    const width = isNaN(parsed) ? 180 : parsed;
    return Math.min(width, 180);
  }, [program?.intro_video_width]);

  if (loading) {
    return (
      <div className="program-detail-page">
        <div className="program-not-found">
          <h2>Loading...</h2>
        </div>
      </div>
    );
  }

  if (error || !program) {
    return (
      <div className="program-detail-page">
        <div className="program-not-found">
          <h2>Program Not Found</h2>
          <p>The program you're looking for doesn't exist.</p>
          <Link to="/programs" className="btn-back">
            View All Programs
          </Link>
        </div>
      </div>
    );
  }

  const features = program.features || [];
  const offers = Array.isArray(program.program_offers) ? program.program_offers : [];

  const formatPriceINR = (val) => {
    const num = Number(val) || 0;
    return '₹' + num.toLocaleString('en-IN');
  };

  const handleBuyNow = async (offer = null) => {
    const programId = program?.program_id || id;
    const hasValidSession = await authService.ensureUserSession();
    if (!hasValidSession) {
      const pid = program?.program_id || id;
      localStorage.setItem('resume_checkout_program_id', pid);
      sessionStorage.setItem('resume_checkout_program_id', pid);
      if (offer?.id) {
        localStorage.setItem('resume_checkout_offer_id', offer.id);
        sessionStorage.setItem('resume_checkout_offer_id', offer.id);
      }
      window.dispatchEvent(new CustomEvent('OPEN_AUTH_MODAL', { detail: { tab: 'login' } }));
      return;
    }
    try {
      const orderData = await programService.createOrder(programId, offer?.id || null);
      const orderId = orderData.order_id;
      const amountPaise = orderData.amount_paise || orderData.amount * 100 || 0;
      const keyId = orderData.key_id || process.env.REACT_APP_RAZORPAY_KEY_ID || '';

      if (!keyId) {
        alert('Razorpay key not configured. Please contact support.');
        return;
      }

      if (typeof window.Razorpay === 'undefined') {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://checkout.razorpay.com/v1/checkout.js';
          script.onload = resolve;
          script.onerror = () => reject(new Error('Failed to load Razorpay checkout'));
          document.body.appendChild(script);
        });
      }

      const options = {
        key: keyId,
        amount: amountPaise,
        currency: 'INR',
        name: 'Get Fit with Daniella',
        description: program.name,
        order_id: orderId,
        handler: async (response) => {
          try {
            const verify = await programService.verifyPayment(
              response.razorpay_order_id,
              response.razorpay_payment_id,
              response.razorpay_signature,
              programId,
              offer?.id || null
            );
            if (verify?.success) {
              alert('Payment successful! Access granted.');
              window.location.href = '/my-programs';
            } else {
              alert('Payment verification failed. Please try again.');
            }
          } catch (e) {
            if (e.response?.status === 401) {
              alert('Authentication error. Please log in again and try the purchase.');
            } else {
              alert('Payment verification error.');
            }
          }
        },
        prefill: {
          email: '',
        },
        theme: { color: '#6a3df0' },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', () => {
        alert('Payment cancelled or failed. Please try again.');
      });
      rzp.open();
    } catch (err) {
      if (err?.response?.status === 400 && err?.response?.data?.message?.includes('already have active access')) {
        alert('You already have access to this program. Redirecting to your dashboard...');
        window.location.href = '/my-programs';
      } else {
        const errorMessage = err?.response?.data?.message || 'Unable to start payment flow.';
        alert(errorMessage);
      }
    }
  };

  const handleDownloadImage = (imageUrl) => {
    if (!imageUrl) return;
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = `${program.name}-exclusive-content.jpg`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const includesBlock = (
    <div className="program-includes">
      <h2>What you get in this program:</h2>
      <ul className="includes-list">
        {features.length > 0 ? (
          features.map((feature, index) => (
            <li key={index}>{feature}</li>
          ))
        ) : (
          <>
            <li>Personalized workout plans tailored to your goals</li>
            <li>Nutrition guidance and meal planning tips</li>
            <li>Expert coaching and support throughout</li>
            <li>Access to exclusive community group</li>
            <li>Progress tracking and regular check-ins</li>
          </>
        )}
      </ul>
    </div>
  );

  return (
    <div className="program-detail-page">
      <div className="program-top-bar">
        <div className="program-top-bar-content">
          <Link to="/programs" className="back-link">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="19" y1="12" x2="5" y2="12"/>
              <polyline points="12 19 5 12 12 5"/>
            </svg>
            Back to Programs
          </Link>
          <h1 className="program-top-title">{program.name}</h1>
        </div>
      </div>

      <div className="program-detail-container">
        <div className="program-detail-layout">
          <div className="program-image-section">
            <div className="program-image-wrapper">
              <img 
                src={program.image || '/placeholder-program.jpg'} 
                alt={program.name}
                className="program-main-image"
              />
            </div>

            {program.hasPurchased && program.download_image && (
              <div className="download-image-section">
                <h3 className="download-image-title">Exclusive Download</h3>
                <div className="download-image-wrapper">
                  <img 
                    src={program.download_image} 
                    alt="Downloadable content"
                    className="download-image"
                  />
                  <div className="download-overlay">
                    <button 
                      className="download-btn"
                      onClick={() => handleDownloadImage(program.download_image)}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="7 10 12 15 17 10"/>
                        <line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                      Download
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="program-content-section">
            <h1 className="program-title">{program.duration} Program: {program.name}</h1>

            <div className="program-welcome">
              <h2>Welcome:</h2>
              <p>{program.description || program.tagline || 'Join this transformative program designed to help you achieve your fitness goals with expert guidance and support.'}</p>
            </div>

            {showIntro ? (
              <div className="intro-and-includes">
                <div
                  className="program-intro-video"
                  style={{ maxWidth: `${introVideoWidth}px`, width: '100%' }}
                >
                  <h3>Watch this first</h3>
                  <ProgramVideoPlayer
                    playback={introPlayback}
                    fallbackUrl={introEmbedUrl || introVideoUrl || ''}
                    title="Program intro video"
                    height={program.intro_video_height || 150}
                    videoClassName="intro-video-frame intro-video-player"
                  />
                </div>
                {includesBlock}
              </div>
            ) : (
              includesBlock
            )}

            {program.hasPurchased ? (
              <div className="program-cta-section">
                <div className="program-cta-price">
                  <span className="price-label">You already own this program</span>
                </div>
                <div className="cta-buttons">
                  <Link className="btn-buy" to="/my-programs">Go to My Programs</Link>
                </div>
              </div>
            ) : offers.length > 0 ? (
              <div className="program-offers">
                <h3 className="offers-title">Choose your plan</h3>
                {offers.map((offer) => (
                  <div key={offer.id} className="offer-card">
                    <div className="offer-left">
                      <div className="offer-label">{offer.label || 'Plan'}</div>
                      {offer.description && <div className="offer-desc">{offer.description}</div>}
                    </div>
                    <div className="offer-right">
                      <div className="offer-price">{formatPriceINR(offer.price_inr)}</div>
                      <button className="btn-buy offer-compact" onClick={() => handleBuyNow(offer)}>
                        {offer.cta_text || 'Buy Now'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="program-cta-section">
                <div className="program-cta-price">
                  <span className="price-label">Price:</span>
                  <span className="price-value">{formatPriceINR(program.price_inr)}</span>
                </div>
                <div className="cta-buttons">
                  <button className="btn-buy" onClick={() => handleBuyNow()}>Buy Now</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProgramDetailPage;

